import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";
import { Bot, ShieldAlert, Save, Loader2, RefreshCw } from "lucide-react";

const STATUS_COLORS = {
  ok: "bg-[color:rgba(0,194,168,0.14)] text-[color:var(--success)]",
  blocked: "bg-[color:rgba(255,171,0,0.16)] text-[#b7791f]",
  error: "bg-[color:rgba(232,93,106,0.14)] text-[color:var(--danger)]",
};

export function AdminAiGateway() {
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState("all");
  const [saving, setSaving] = useState(false);
  const [terms, setTerms] = useState("");

  const load = () => {
    api.get("/ai-gateway/settings").then(({ data }) => { setSettings(data); setTerms((data.blocked_terms || []).join(", ")); }).catch(() => {});
    api.get("/ai-gateway/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/ai-gateway/logs", { params: { limit: 50, ...(status !== "all" ? { status } : {}) } }).then(({ data }) => setLogs(data.items || [])).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/ai-gateway/settings", {
        enabled: settings.enabled, log_prompts: settings.log_prompts,
        blocked_terms: terms.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("AI gateway settings saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  if (!settings) return null;
  return (
    <div className="space-y-4" data-testid="admin-ai-gateway">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[["Total calls", stats?.total ?? "—"], ["Successful", stats?.ok ?? "—"], ["Blocked", stats?.blocked ?? "—"], ["Errors", stats?.errors ?? "—"], ["Avg latency", stats ? `${stats.avg_latency_ms} ms` : "—"]].map(([l, v]) => (
          <div key={l} className="glass-card p-3"><div className="font-display text-xl text-[color:var(--ink)] tabular-nums">{v}</div><div className="text-[11px] text-[color:var(--ink-3)]">{l}</div></div>
        ))}
      </div>

      <div className="glass-card p-4 space-y-3">
        <h3 className="text-sm font-semibold text-[color:var(--ink)] flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-[color:var(--brand-ocean)]" />Governance policy</h3>
        <div className="flex items-center gap-6 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-[color:var(--ink-2)]">
            <Switch data-testid="gw-enabled" checked={settings.enabled} onCheckedChange={(v) => setSettings((s) => ({ ...s, enabled: v }))} />AI features enabled
          </label>
          <label className="flex items-center gap-2 text-sm text-[color:var(--ink-2)]">
            <Switch data-testid="gw-log-prompts" checked={settings.log_prompts} onCheckedChange={(v) => setSettings((s) => ({ ...s, log_prompts: v }))} />Log prompt previews
          </label>
        </div>
        <div>
          <Label className="text-xs">Blocked terms (comma-separated — prompts containing these are rejected)</Label>
          <Textarea data-testid="gw-blocked-terms" rows={2} className="mt-1.5 bg-[color:var(--surface)] text-sm" value={terms} onChange={(e) => setTerms(e.target.value)} placeholder="confidential, project-x" />
        </div>
        <Button data-testid="gw-save-settings" onClick={save} disabled={saving} size="sm" className="bg-[color:var(--brand-ocean)] text-white">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Save settings
        </Button>
      </div>

      <div className="glass-card p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[color:var(--ink)] flex items-center gap-2"><Bot className="w-4 h-4 text-[color:var(--brand-mint)]" />Request log</h3>
          <div className="flex items-center gap-2">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-8 w-28 text-xs bg-[color:var(--surface)]" data-testid="gw-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="ok">OK</SelectItem><SelectItem value="blocked">Blocked</SelectItem><SelectItem value="error">Error</SelectItem></SelectContent>
            </Select>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={load}><RefreshCw className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
        <div className="overflow-auto max-h-[420px]">
          <table className="w-full text-xs" data-testid="gw-logs-table">
            <thead><tr className="text-left text-[color:var(--ink-3)] border-b border-[color:var(--glass-border)]">
              <th className="py-2 pr-3">Time</th><th className="py-2 pr-3">User</th><th className="py-2 pr-3">Purpose</th><th className="py-2 pr-3">Model</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Latency</th><th className="py-2">Prompt</th>
            </tr></thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} data-testid="gw-log-row" className="border-b border-[color:var(--glass-border)] last:border-0">
                  <td className="py-1.5 pr-3 whitespace-nowrap text-[color:var(--ink-3)]">{l.ts ? new Date(l.ts).toLocaleString() : "—"}</td>
                  <td className="py-1.5 pr-3 text-[color:var(--ink-2)]">{l.user_email || "—"}</td>
                  <td className="py-1.5 pr-3"><Badge variant="outline" className="text-[10px]">{l.purpose}</Badge></td>
                  <td className="py-1.5 pr-3 text-[color:var(--ink-3)]">{l.model}</td>
                  <td className="py-1.5 pr-3"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${STATUS_COLORS[l.status] || ""}`}>{l.status}</span></td>
                  <td className="py-1.5 pr-3 tabular-nums text-[color:var(--ink-2)]">{l.duration_ms} ms</td>
                  <td className="py-1.5 text-[color:var(--ink-3)] max-w-[280px] truncate" title={l.prompt_preview}>{l.prompt_preview || "(not logged)"}</td>
                </tr>
              ))}
              {!logs.length && <tr><td colSpan={7} className="py-6 text-center text-[color:var(--ink-3)]">No AI calls logged yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
